/**
 * DeckExportModal: Modal mit Live-Vorschau für Deck-Export
 * - Format-Tabs (Text, Cardmarket, MTGA, CSV) via ui/ExportModal (Kopieren/Download
 *   sowie Format-Tabs/Statistik-Zeile/Live-Vorschau-Textarea liefert der Baustein selbst)
 * - Token-Modus (Ohne/Mit/Nur Tokens) via ui/Form.Segmented, nur wenn das Deck Tokens hat.
 *   Bei "Nur Tokens" sind die Deck-Karten-Optionen wirkungslos und werden ausgegraut.
 * - "Nur fehlende Karten" / "Ohne Basic Länder" / je ausgeschlossener Kategorie ein Toggle
 *   via ui/ExportModal.Option
 *
 * initialTokenMode: mit welchem Token-Modus das Modal beim Öffnen startet. Der obere
 * Editor-Export öffnet mit 'none' (unverändertes Verhalten), der Export-Button der
 * Token-Kachel mit 'only'. Wird bei jedem Öffnen frisch übernommen.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EXPORT_FORMATS, generateExportContent, buildExportFilename } from '../DeckExport';
import ExportModal from '../../../ui/ExportModal';
import Form from '../../../ui/Form';
import SectionHeader from '../../../ui/SectionHeader';
import { formatCategoryName } from '../../../ui/formatCategoryName';
import useCardSets from '../../../hooks/useCardSets';
import useCardmarketRefs from '../../../hooks/useCardmarketRefs';
import { useWantlistSummary } from './bridge/WantlistRow';

const FORMAT_TABS = [
  { value: EXPORT_FORMATS.TEXT, label: 'Text' },
  { value: EXPORT_FORMATS.CARDMARKET, label: 'Cardmarket' },
  { value: EXPORT_FORMATS.MTGA, label: 'MTGA' },
  { value: EXPORT_FORMATS.CSV, label: 'CSV' },
];

const TOKEN_MODE_OPTIONS = [
  { value: 'none', label: 'Ohne' },
  { value: 'with', label: 'Mit' },
  { value: 'only', label: 'Nur' },
];

export default function DeckExportModal({ deck, isOpen, onClose, excludedCategoryNames = new Set(), initialTokenMode = 'none', isOwnProfile = false }) {
  const [format, setFormat] = useState(EXPORT_FORMATS.TEXT);
  const { friendsCount } = useWantlistSummary(deck, isOwnProfile);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [excludeBasicLands, setExcludeBasicLands] = useState(false);
  const [includedExcludedCategories, setIncludedExcludedCategories] = useState(new Set());
  const [tokenMode, setTokenMode] = useState(initialTokenMode);

  const { data: setData } = useCardSets(isOpen);
  const setMap = useMemo(() => setData?.sets || {}, [setData]);

  const lockedIds = useMemo(
    () => [...new Set((deck?.cards || []).filter(c => c.printLocked && c.id).map(c => c.id))],
    [deck?.cards]
  );
  const { data: refData } = useCardmarketRefs(lockedIds, isOpen);
  const cardmarketRefs = useMemo(() => refData?.refs || {}, [refData]);

  const tokenCount = useMemo(
    () => new Set((deck?.tokens || []).map(t => t.id)).size,
    [deck?.tokens]
  );
  const hasTokens = tokenCount > 0;
  const cardOptionsDisabled = tokenMode === 'only';

  // Beim Öffnen den gewünschten Startmodus übernehmen (z.B. 'only' vom Token-Kachel-Button).
  useEffect(() => {
    if (isOpen) setTokenMode(hasTokens ? initialTokenMode : 'none');
  }, [isOpen, initialTokenMode, hasTokens]);

  const toggleIncludedExcluded = useCallback((name) => {
    setIncludedExcludedCategories(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // Tatsächlich ausgeschlossene Kategorien: alle excluded MINUS die vom User eingeschlossenen
  const activeExcludedCategories = useMemo(() => {
    const result = new Set();
    for (const name of excludedCategoryNames) {
      if (!includedExcludedCategories.has(name)) {
        result.add(name);
      }
    }
    return result;
  }, [excludedCategoryNames, includedExcludedCategories]);

  // Live-Preview generieren
  const exportContent = useMemo(() => {
    if (!deck) return '';
    return generateExportContent(deck, format, { onlyMissing, excludedCategories: activeExcludedCategories, excludeBasicLands, tokenMode, setMap, cardmarketRefs });
  }, [deck, format, onlyMissing, activeExcludedCategories, excludeBasicLands, tokenMode, setMap, cardmarketRefs]);

  // Statistiken berechnen (Deck-Karten; Tokens werden separat gezählt)
  const stats = useMemo(() => {
    if (!deck?.cards) return { totalCards: 0, uniqueCards: 0 };

    const cards = deck.cards.filter(c => {
      if (activeExcludedCategories.has(c.category?.toLowerCase())) return false;
      if (excludeBasicLands) {
        const superTypes = c.superTypes || [];
        const types = c.types || [];
        if (superTypes.some(st => st.toLowerCase() === 'basic') && types.some(t => t.toLowerCase() === 'land')) return false;
      }
      return true;
    });
    let totalCards = 0;
    let uniqueCards = 0;

    for (const card of cards) {
      const qty = card.quantity || 1;
      if (onlyMissing) {
        const owned = card.totalOwnedQuantity ?? card.ownedQuantity ?? 0;
        const missing = Math.max(0, qty - owned);
        if (missing > 0) {
          totalCards += missing;
          uniqueCards++;
        }
      } else {
        totalCards += qty;
        uniqueCards++;
      }
    }

    return { totalCards, uniqueCards };
  }, [deck?.cards, onlyMissing, activeExcludedCategories, excludeBasicLands]);

  // Statistik-Text je nach Token-Modus
  const statsText = useMemo(() => {
    if (tokenMode === 'only') {
      return `${tokenCount} Tokens`;
    }
    const base = onlyMissing
      ? `${stats.totalCards} fehlende Karten (${stats.uniqueCards} verschiedene)`
      : `${stats.totalCards} Karten (${stats.uniqueCards} verschiedene)`;
    return tokenMode === 'with' && tokenCount > 0 ? `${base} + ${tokenCount} Tokens` : base;
  }, [tokenMode, tokenCount, onlyMissing, stats]);

  // Nichts zu exportieren: je nach Token-Modus zählen Karten und/oder Tokens.
  const exportIsEmpty = tokenMode === 'only'
    ? tokenCount === 0
    : stats.totalCards === 0 && (tokenMode !== 'with' || tokenCount === 0);

  if (!deck) return null;

  return (
    <ExportModal
      open={isOpen}
      onOpenChange={(next) => { if (!next) onClose(); }}
      title="Deck exportieren"
      formats={FORMAT_TABS}
      format={format}
      onFormatChange={setFormat}
      stats={statsText}
      content={exportContent}
      filename={(f) => buildExportFilename(deck, f, { onlyMissing, tokenMode })}
      empty={exportIsEmpty}
    >
      {friendsCount > 0 && (
        <div className="de-export-friends-hint">
          <span className="de-export-friends-hint__count">{friendsCount}</span>{' '}
          {friendsCount === 1
            ? 'deiner fehlenden Karten besitzt ein Freund —'
            : 'deiner fehlenden Karten besitzen Freunde —'}{' '}
          <Link to={`/wantlist?deckId=${encodeURIComponent(deck?.id)}`} onClick={onClose}>
            erst in die Wantlist schauen?
          </Link>
        </div>
      )}
      {hasTokens && (
        <div className="de-export-tokens">
          <span className="de-export-tokens__label">Tokens</span>
          <Form.Segmented value={tokenMode} onChange={setTokenMode} options={TOKEN_MODE_OPTIONS} />
        </div>
      )}
      <ExportModal.Option checked={onlyMissing} disabled={cardOptionsDisabled} onChange={(e) => setOnlyMissing(e.target.checked)}>
        Nur fehlende Karten exportieren
      </ExportModal.Option>
      <ExportModal.Option checked={excludeBasicLands} disabled={cardOptionsDisabled} onChange={(e) => setExcludeBasicLands(e.target.checked)}>
        Ohne Basic Länder
      </ExportModal.Option>
      {excludedCategoryNames.size > 0 && (
        <>
          <SectionHeader size="sm">Nicht-im-Deck-Kategorien</SectionHeader>
          {[...excludedCategoryNames].map(name => (
            <ExportModal.Option key={name} checked={includedExcludedCategories.has(name)} disabled={cardOptionsDisabled} onChange={() => toggleIncludedExcluded(name)}>
              <span>{formatCategoryName(name)}</span>&nbsp;mit exportieren
            </ExportModal.Option>
          ))}
        </>
      )}
    </ExportModal>
  );
}
